import React, { useState } from 'react';
import { RefreshCw, Trash2, Bell, Check, Loader, Target, ExternalLink, Copy, ChevronRight } from '../../icons';
import { unwrapProxySearchUrl } from '../../utils/propertyPlatforms';
import Button from '../ui/Button';
import Menu from '../ui/Menu';
import Sheet from '../ui/Sheet';
import Toggle from '../ui/Toggle';
import PlatformBadge from '../ui/PlatformBadge';
import { cn } from '../ui/cn';
import { timeAgo } from '../../utils/csvExport';

/**
 * Format a price for display, handling both numbers and pre-formatted strings.
 * - Numbers (from BuySellCyprus): formatted with toLocaleString → "€350,000"
 * - Strings (from Bazaraki API): displayed as-is → "€1.300"
 * - Falsy/zero: "N/A"
 */
const formatPrice = (price) => {
  if (price == null || price === '' || price === 0) return 'N/A';
  if (typeof price === 'number') return `€${price.toLocaleString()}`;
  return `€${price}`;
};

const SearchCard = ({
  search,
  onScan,
  onDelete,
  isScanning,
  newlyFoundItems = [],
  formatDate,
  onTrackItem,
  onToggleOpportunityTracking,
  opportunityStats,
  onNavigateToOpportunities,
}) => {
  const [trackingItems, setTrackingItems] = useState(new Set());
  const [trackedItems, setTrackedItems] = useState(new Set());
  const [isTogglingOpportunity, setIsTogglingOpportunity] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const handleToggleOpportunityTracking = async () => {
    setIsTogglingOpportunity(true);
    try {
      await onToggleOpportunityTracking(search.id, !search.opportunityTracking?.enabled);
    } catch (error) {
      console.error('Error toggling opportunity tracking:', error);
    } finally {
      setIsTogglingOpportunity(false);
    }
  };

  const displayUrl = unwrapProxySearchUrl(search.searchUrl || '');
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = displayUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  };

  const handleTrackItem = async (itemUrl, itemId) => {
    setTrackingItems(prev => new Set([...prev, itemId]));
    
    try {
      await onTrackItem(itemUrl);
      setTrackedItems(prev => new Set([...prev, itemId]));
      setTrackingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    } catch (error) {
      console.error('Error tracking item:', error);
      setTrackingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const oppEnabled = !!search.opportunityTracking?.enabled;
  const resultsCount = search.lastSeenItemIds?.length;
  const lastScan = search.lastScanTimestamp;

  return (
    <article className="min-w-0 bg-card border border-line rounded-card shadow-card p-4 flex flex-col gap-3" data-testid="search-card">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <PlatformBadge platform={search.platform} />
            <h3 className="min-w-0 text-[16px] font-semibold text-primary leading-snug line-clamp-2 break-words" title={search.name}>{search.name}</h3>
          </div>
          <div className="mt-1 flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={handleCopyUrl}
              className="group flex items-center gap-1.5 min-w-0 text-xs text-muted hover:text-secondary text-left"
              title={`${displayUrl}\n\nClick to copy`}
            >
              <span className="truncate">{displayUrl}</span>
              {urlCopied ? (
                <span className="text-accent flex items-center gap-0.5 flex-shrink-0"><Check className="w-3 h-3" /> Copied</span>
              ) : (
                <Copy className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100" />
              )}
            </button>
          </div>
        </div>
        <Menu
          label={`Actions for ${search.name}`}
          items={[
            displayUrl && { label: 'Open search', icon: ExternalLink, href: displayUrl },
            { label: urlCopied ? 'Copied' : 'Copy URL', icon: Copy, onSelect: handleCopyUrl },
            { label: 'Delete', icon: Trash2, danger: true, onSelect: () => setShowDeleteConfirm(true) },
          ]}
        />
      </div>

      {/* Stat row + scan */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-xs text-secondary flex-wrap min-w-0">
          <span title={lastScan ? formatDate(lastScan) : undefined}>
            {lastScan ? `Last scan ${timeAgo(lastScan) || formatDate(lastScan)}` : 'Never scanned'}
          </span>
          {typeof resultsCount === 'number' && (
            <>
              <span className="text-muted">·</span>
              <span>{resultsCount} results</span>
            </>
          )}
          {newlyFoundItems && newlyFoundItems.length > 0 && (
            <>
              <span className="text-muted">·</span>
              <span className="px-1.5 py-0.5 rounded-md bg-accent-soft text-accent font-semibold">{newlyFoundItems.length} new</span>
            </>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={() => onScan(search)} disabled={isScanning} loading={isScanning}>
          {!isScanning && <RefreshCw className="w-4 h-4" />}
          {isScanning ? 'Scanning' : 'Scan now'}
        </Button>
      </div>

      {/* New listings found section */}
      {newlyFoundItems && newlyFoundItems.length > 0 && (
        <div className="rounded-xl border border-accent/30 bg-page p-3">
          <h4 className="text-xs font-semibold text-accent mb-2 flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5" />
            New listings ({newlyFoundItems.length})
          </h4>
          <ul className="space-y-2">
            {newlyFoundItems.map((item) => {
              const tracking = trackingItems.has(item.id);
              const tracked = trackedItems.has(item.id);
              return (
                <li key={item.id} className="flex items-center gap-3">
                  {item.imageURL || item.image ? (
                    <img src={item.imageURL || item.image} alt="" className="w-12 h-10 rounded-md object-cover flex-shrink-0 bg-card-2" loading="lazy" referrerPolicy="no-referrer" />
                  ) : null}
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 hover:underline">
                    <span className="block text-sm text-primary truncate">{item.title || `Item ID: ${item.id}`}</span>
                    <span className="block text-xs text-accent tabular">{formatPrice(item.price)}</span>
                  </a>
                  <Button
                    size="sm"
                    variant={tracked ? 'ghost' : 'secondary'}
                    onClick={() => handleTrackItem(item.url, item.id)}
                    disabled={tracking || tracked}
                    className={cn(tracked && 'text-accent')}
                  >
                    {tracking ? (
                      <><Loader className="w-3.5 h-3.5 animate-spin" /> Tracking</>
                    ) : tracked ? (
                      <><Check className="w-3.5 h-3.5" /> Tracked</>
                    ) : (
                      'Track'
                    )}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* No new items message */}
      {newlyFoundItems && newlyFoundItems.length === 0 && !isScanning && lastScan && (
        <p className="text-xs text-muted">No new items found in the last scan for "{search.name}".</p>
      )}

      {/* Opportunity Tracking */}
      <div className="rounded-xl border border-line bg-page p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Target className={cn('w-4 h-4 flex-shrink-0', oppEnabled ? 'text-accent' : 'text-muted')} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-primary">Opportunity tracking</div>
              <div className="text-xs text-muted truncate">Scans daily for undervalued properties based on AI analysis</div>
            </div>
          </div>
          <Toggle checked={oppEnabled} onChange={handleToggleOpportunityTracking} disabled={isTogglingOpportunity} label="Opportunity tracking" size="sm" />
        </div>

        {oppEnabled && opportunityStats && (
          <div className="mt-3 pt-3 border-t border-line flex items-center justify-between gap-3 text-xs">
            <div className="text-secondary">
              <span className="text-accent font-semibold">{opportunityStats.active || 0}</span> active
              {opportunityStats.lastScan && <span className="text-muted"> · last scan {formatDate(opportunityStats.lastScan)}</span>}
            </div>
            {opportunityStats.active > 0 && onNavigateToOpportunities && (
              <button type="button" onClick={onNavigateToOpportunities} className="inline-flex items-center gap-0.5 text-accent font-medium hover:underline whitespace-nowrap flex-shrink-0">
                View {opportunityStats.active} opportunit{opportunityStats.active === 1 ? 'y' : 'ies'} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {oppEnabled && (!opportunityStats || opportunityStats.active === 0) && (
          <p className="mt-2 text-xs text-muted italic">No opportunities detected yet. Check back after the next scan.</p>
        )}
      </div>

      {/* Delete confirmation */}
      <Sheet isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} mode="dialog" size="sm" title="Delete tracked search">
        <p className="text-sm text-primary">
          Are you sure you want to delete <strong>"{search.name}"</strong>?
        </p>
        <p className="text-xs text-secondary mt-2">This will remove the search and all scan history. This cannot be undone.</p>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            variant="danger-solid"
            onClick={() => {
              setShowDeleteConfirm(false);
              onDelete(search.id);
            }}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </Button>
        </div>
      </Sheet>
    </article>
  );
};

export default SearchCard;
